import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { PublicClientApplication } from '@azure/msal-browser'
import { Button } from '../common/components/ui/button'
import { appConfig } from '../config/runtime-config'

const clientId = appConfig.azureClientId
const tenantId = appConfig.azureTenantId
const authority = `https://login.microsoftonline.com/${tenantId}`
const redirectUri = appConfig.azureRedirectUri

const sessionIdTokenKey = 'obs_microsoft_id_token'
const sessionEmailKey = 'obs_microsoft_email'

const normalizeUserId = (value) => String(value || '').trim().toLowerCase()
const extractMicrosoftUserId = (claims = {}) =>
  normalizeUserId(
    claims.email ||
    claims.upn ||
    claims.preferred_username ||
    claims.unique_name ||
    ''
  )

const pca = clientId
  ? new PublicClientApplication({
    auth: {
      clientId,
      authority,
      redirectUri,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
  })
  : null

let pcaInitializePromise = null
/**
 * Initializes the shared MSAL client exactly once and reuses the same promise
 * for concurrent callers. This prevents duplicate MSAL startup work during
 * route guards, redirect handling, and interactive sign-in.
 */
const initializePca = async () => {
  if (!pca) return
  if (!pcaInitializePromise) {
    pcaInitializePromise = pca.initialize().catch((err) => {
      pcaInitializePromise = null
      throw err
    })
  }
  await pcaInitializePromise
}

/**
 * Decodes the payload section of a JWT without verifying its signature.
 * The frontend uses this only for local claim inspection such as deriving the
 * current user identifier and checking token expiry before reuse.
 */
const parseJwtPayload = (token) => {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return {}
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    return JSON.parse(window.atob(padded))
  } catch (_error) {
    return {}
  }
}

/**
 * Applies lightweight client-side checks before a cached Microsoft ID token is
 * reused. Backend APIs still perform real validation; this avoids obviously
 * expired or mismatched tokens lingering in local storage.
 */
const isValidMicrosoftToken = (token) => {
  if (!token) return false
  const claims = parseJwtPayload(token)
  const aud = String(claims.aud || '')
  const exp = Number(claims.exp || 0)
  const now = Math.floor(Date.now() / 1000)
  if (!aud || aud !== clientId) return false
  if (!exp || exp <= now) return false
  return true
}

/**
 * Persists the Microsoft session data needed by the rest of the SPA: the raw
 * ID token for authenticated API requests and the normalized user email for
 * app-level identity checks.
 */
const persistMicrosoftSession = (idToken) => {
  if (!idToken) return
  const claims = parseJwtPayload(idToken)
  const email = extractMicrosoftUserId(claims)
  window.localStorage.setItem(sessionIdTokenKey, idToken)
  window.localStorage.setItem(sessionEmailKey, email)
}

/**
 * Clears all locally cached authentication state for the current browser
 * session.
 */
const clearSession = () => {
  window.localStorage.removeItem(sessionIdTokenKey)
  window.localStorage.removeItem(sessionEmailKey)
}

const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
}

/**
 * Shared presentation shell for auth routes. This keeps the login, first-time
 * access, and password reset screens visually aligned while auth behavior
 * stays inside the dedicated Microsoft view.
 */
function Layout({ title, subtitle, children }) {
  return (
    <div className="h-screen w-screen flex flex-col items-center justify-center" style={{ backgroundImage: 'url(/login-background.png)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <img src="/zinkworks-logo-white.png" alt="Zinkworks logo" className="mx-auto w-80" />
      <div className="flex flex-col justify-around w-[90%] max-w-md rounded-2xl bg-white/90 backdrop-blur-sm border border-white/20 shadow-lg">
        {/* <div className="h-1 bg-gradient-to-r from-[#007D8B] via-[#CCE5E8] to-transparent" /> */}
        <div className="flex flex-col gap-8 p-6 text-center">
          <div className="text-xl font-bold text-[#0b1f3a]">{title}</div>
          <div className="text-sm text-[#0f172a]">{subtitle}</div>
        </div>
        <div className="p-6 flex flex-col gap-4">{children}</div>
      </div>
    </div>
  )
}

Layout.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
}

/**
 * Handles Microsoft sign-in UI and the two MSAL entry paths used here:
 * redirect completion on page load and popup-based sign-in when the user
 * actively clicks the button.
 */
function MicrosoftAuthView({ title, subtitle = 'Sign in with your Microsoft work account to continue.' }) {
  const [error, setError] = useState('')
  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    if (!pca) {
      setError('Microsoft auth is not configured. Set REACT_APP_AZURE_CLIENT_ID and REACT_APP_AZURE_TENANT_ID at runtime.')
      return
    }

    initializePca()
      .then(() => pca.handleRedirectPromise())
      .then((result) => {
        if (result?.idToken) {
          persistMicrosoftSession(result.idToken)
          window.location.assign('/')
        }
      })
      .catch((err) => {
        console.error('Microsoft redirect sign-in failed', err)
        setError('Microsoft redirect sign-in failed. Please try again.')
      })
  }, [])

  const signIn = async () => {
    if (!pca) return
    setIsBusy(true)
    setError('')
    try {
      await initializePca()
      const response = await pca.loginPopup(loginRequest)
      if (response?.idToken) {
        persistMicrosoftSession(response.idToken)
        window.location.assign('/')
        return
      }
      setError('No identity token was returned by Microsoft.')
    } catch (err) {
      const code = err?.errorCode || err?.code || 'unknown_error'
      const message = err?.errorMessage || err?.message || 'No details from MSAL.'
      console.error('Microsoft sign-in failed', err)
      setError(`Microsoft sign-in failed.\n(${code}) ${message}`)
    } finally {
      setIsBusy(false)
    }
  }

  return (
    <Layout title={title} subtitle={subtitle}>
      {error ? <div className="text-red-600 bg-red-100 border border-red-400 rounded-md p-2 text-sm">{error}</div> : null}
      <Button
        // variant="outline"
        type="button"
        onClick={signIn}
        disabled={isBusy || !pca}
        className="h-11 flex items-center gap-2"
      >
        <img src="/microsoft-icon.svg" alt="" width={20} height={20} />
        {isBusy ? 'Signing in...' : 'Continue with Microsoft'}
      </Button>
    </Layout>
  )
}

MicrosoftAuthView.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
}

/**
 * Login entry point for returning users. It also removes leftover query
 * parameters after MSAL redirect handling so the route remains stable.
 */
const LoginView = () => {
  useEffect(() => {
    if (window.location.pathname === '/login' && window.location.search) {
      window.history.replaceState({}, '', '/login')
    }
  }, [])

  return <MicrosoftAuthView title="Login to continue" />
}

/**
 * First-time access entry point. It reuses the Microsoft sign-in flow but with
 * copy tailored to onboarding rather than standard login.
 */
const SignupView = () => {
  return (
    <MicrosoftAuthView
      title="First-time access"
      subtitle="Use your Microsoft work account the first time to create access."
    />
  )
}

/**
 * Password reset route wrapper. Microsoft owns the underlying credential flow;
 * this screen just presents the standard auth shell with reset-specific copy.
 */
const PwdResetView = () => {
  return <MicrosoftAuthView title="Password Reset" />
}

/**
 * Returns the cached Microsoft ID token when it is still usable. Invalid or
 * expired tokens are discarded to avoid sending stale credentials to the API.
 */
const getIdToken = () => {
  const msToken = window.localStorage.getItem(sessionIdTokenKey) || ''
  if (msToken && isValidMicrosoftToken(msToken)) return msToken
  if (msToken && !isValidMicrosoftToken(msToken)) {
    clearSession()
  }

  return ''
}

/**
 * Resolves the current signed-in user identifier for API calls and UI logic.
 * It prefers the normalized email cached beside the token and falls back to
 * decoding the current token when necessary.
 */
const getCurrentUserId = () => {
  const msEmail = normalizeUserId(window.localStorage.getItem(sessionEmailKey) || '')
  if (msEmail) return msEmail

  const token = getIdToken()
  const claims = parseJwtPayload(token)
  return extractMicrosoftUserId(claims)
}

/**
 * Signs the user out locally and, when possible, terminates the Microsoft
 * session via MSAL before returning the browser to the login route.
 */
const logout = async () => {
  clearSession()

  if (pca) {
    try {
      await initializePca()
      await pca.logoutPopup({ postLogoutRedirectUri: `${window.location.origin}/login` })
    } catch (_error) {
    }
  }

  window.location.assign('/login')
}

export {
  LoginView,
  SignupView,
  PwdResetView,
  getIdToken,
  getCurrentUserId,
  logout,
}
